import { logError } from '@zg-rentals/log-error';
import ExtensionsConstants from '../constants/ExtensionsConstants';

const MIN_IMAGE_WIDTH = 330;
const MIN_IMAGE_HEIGHT = 220;

const validateFileDimension = async (file: File) => {
  return await new Promise<{ width: number; height: number } | null>((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      resolve(null); // Error loading image
      URL.revokeObjectURL(objectUrl);
    };
  }).then((dimensions) => {
    if (!dimensions) {
      // code: 'image-load-error',
      return 'Failed to load the image to check dimensions. Try again.';
    }

    if (dimensions.width < MIN_IMAGE_WIDTH || dimensions.height < MIN_IMAGE_HEIGHT) {
      // code: 'image-too-small',
      return `Photos must be at least ${MIN_IMAGE_WIDTH}x${MIN_IMAGE_HEIGHT} pixels. Select a larger photo to upload`;
    }
    return undefined; // Image is valid
  });
};

export const photoUtils = {
  hasValidExtension(file: { name: string }) {
    if (file && file.name) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const extensionsWhitelist = Object.values(ExtensionsConstants);
      if (extension) {
        return extensionsWhitelist.includes(extension);
      }
      return false;
    }
    return false;
  },

  preloadImages(fileList: Array<Blob>) {
    const promiseArr: Array<Promise<unknown>> = [];

    fileList.forEach((file) => {
      const p = new Promise((resolve) => {
        const img = new Image();
        const fileBlob = URL.createObjectURL(file);

        // handlers
        img.onload = () => {
          URL.revokeObjectURL(fileBlob);
          resolve(file);
        };

        img.onerror = () => {
          logError({
            error: new Error('image could not be loaded'),
            errorType: 'PhotoUtilsError',
            context: 'photoUtils.preloadImages.onerror',
          });

          resolve(false);
        };

        // set source
        img.src = fileBlob;
      });

      // append promises
      promiseArr.push(p);
    });

    if (promiseArr.length > 0) {
      return Promise.all(promiseArr)
        .then((responseList) => {
          // filter list with only loaded files
          responseList = responseList.filter((response) => {
            if (!response) {
              // TODO: respond with a list of files that have errors
              return false;
            }

            return response;
          });

          return responseList;
        })
        .catch((error) => {
          logError({
            error,
            errorType: 'PhotoUtilsError',
            context: 'photoUtils.preloadImages.catch',
          });
        });
    }

    return Promise.resolve();
  },

  filterForHEIC(
    fileList: Array<Blob>,
    onDropRejected: (errorData?: Array<{ errors?: Array<{ message: string; code?: string }> }>) => void,
  ) {
    let heicDetected = false;

    const heicFilteredAcceptedFiles = fileList.filter((file) => {
      const isFileTypeHEIC = file?.type === 'image/heic';
      heicDetected = isFileTypeHEIC || heicDetected;
      return !isFileTypeHEIC;
    });

    if (heicDetected) {
      onDropRejected([
        {
          errors: [
            {
              message:
                'HEIC format is not supported on web. Please download the Zillow Rental Manager App to upload, or convert the files to .png, .jpg, .jpeg, or .gif to upload.',
            },
          ],
        },
      ]);
    }

    return heicFilteredAcceptedFiles;
  },

  async filterForInvalidDimensions(
    fileList: Array<Blob>,
    onDropRejected: (errorData?: Array<{ errors?: Array<{ message: string; code?: string }> }>) => void,
  ) {
    const validatedAcceptedFiles: Array<Blob> = [];
    const errorArray: Array<string> = [];

    for (const file of fileList) {
      const error = await validateFileDimension(file as File);

      if (error) {
        errorArray.push(error);
      } else {
        validatedAcceptedFiles.push(file);
      }
    }

    if (errorArray.length > 0) {
      new Set(errorArray).forEach((err) => {
        onDropRejected([
          {
            errors: [
              {
                message: err,
              },
            ],
          },
        ]);
      });
    }

    return validatedAcceptedFiles;
  },

  async filterForValidFiles(
    fileList: Array<Blob>,
    onDropRejected: (errorData?: Array<{ errors?: Array<{ message: string; code?: string }> }>) => void,
  ) {
    return await this.filterForInvalidDimensions(this.filterForHEIC(fileList, onDropRejected), onDropRejected);
  },
};
